import { weekStart } from 'src/features/appointments/calendarUtils';
import { formatDateKey, formatDateShort } from 'src/shared/utils/datetime';
import type { ExpenseCategory } from './types';

export interface CategoryChoice {
  id: number;
  label: string;
}

/**
 * What the category select of the expense form offers.
 *
 * Only active categories, except the one the expense being edited is already
 * filed under: an expense keeps its archived category through any other edit
 * (the API allows it), and a select that cannot show the current value would
 * silently move the expense to the first option on save.
 */
export const categoryChoices = (
  categories: ExpenseCategory[],
  currentId?: number
): CategoryChoice[] => {
  const choices = categories
    .filter((category) => category.isActive)
    .map((category) => ({ id: category.id, label: category.name }));
  const current = categories.find((category) => category.id === currentId);
  if (current && !current.isActive) {
    choices.push({ id: current.id, label: `${current.name} (archivada)` });
  }
  return choices;
};

/** The colour the period's result is shown in: a loss must not read like a gain. */
export const resultTone = (result: number): 'success' | 'danger' | 'muted' => {
  if (result > 0) return 'success';
  if (result < 0) return 'danger';
  return 'muted';
};

/**
 * The ranges the finance screen offers in one tap. `fechas` is any other range,
 * picked by hand.
 */
export type RangePreset = 'hoy' | 'semana' | 'mes' | 'fechas';

export interface DateRange {
  from: string;
  to: string;
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The range a preset names, ending today.
 *
 * `today` is the business's own date (`todayKey(business.timezone)`), not the
 * browser's: at 20:00 in Guayaquil the browser of an owner travelling abroad
 * may already be on tomorrow, and "Hoy" would show an empty day. "Mes" is the
 * month so far rather than the last 30 days — the question is how this month is
 * going, and a rolling window answers a different one every day.
 */
export const presetRange = (preset: Exclude<RangePreset, 'fechas'>, today: string): DateRange => {
  if (preset === 'hoy') return { from: today, to: today };
  if (preset === 'semana') return { from: weekStart(today), to: today };
  return { from: `${today.slice(0, 7)}-01`, to: today };
};

/**
 * The period the URL asks for.
 *
 * `?desde&hasta` is a range picked by hand; otherwise `?periodo=hoy|semana`
 * names a preset, and no param at all is the month so far. A preset stays a
 * preset in the URL instead of being frozen into dates, so a bookmarked "Hoy"
 * is still today tomorrow.
 */
export const periodFromParams = (
  params: URLSearchParams,
  today: string
): { preset: RangePreset; range: DateRange } => {
  const from = params.get('desde');
  const to = params.get('hasta');
  if (from && to && DATE_KEY.test(from) && DATE_KEY.test(to)) {
    // A hand-edited URL may have them the wrong way round; the API would 422.
    return { preset: 'fechas', range: from <= to ? { from, to } : { from: to, to: from } };
  }
  const period = params.get('periodo');
  const preset = period === 'hoy' || period === 'semana' ? period : 'mes';
  return { preset, range: presetRange(preset, today) };
};

/** "27 sept 2026", or "1 sept – 27 sept 2026": the period the figures belong to. */
export const formatRange = ({ from, to }: DateRange): string => {
  if (from === to) return formatDateShort(from);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const start = sameYear
    ? formatDateKey(from, { day: 'numeric', month: 'short' })
    : formatDateShort(from);
  return `${start} – ${formatDateShort(to)}`;
};

export interface DayGroup<T> {
  /** `YYYY-MM-DD`. */
  day: string;
  items: T[];
}

/**
 * Items bucketed by the day they belong to, in the order the days first appear.
 *
 * The till is read a day at a time — "did today's cash add up?" — so a long
 * range is broken into days instead of one undifferentiated column of amounts.
 */
export const groupByDay = <T>(items: T[], dayOf: (item: T) => string): DayGroup<T>[] => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const day = dayOf(item);
    const group = groups.get(day);
    if (group) group.push(item);
    else groups.set(day, [item]);
  }
  return [...groups].map(([day, grouped]) => ({ day, items: grouped }));
};
